import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useBot } from '../../context/BotContext';
import { useAuth } from '../../context/AuthContext';
import { isBotConfigured } from '../../services/botApi';

const { FiShield, FiTrendingUp, FiSettings, FiLock, FiEdit2, FiBell, FiBellOff, FiAward, FiEye, FiTrendingDown, FiTrash2, FiAlertTriangle, FiDatabase, FiTarget, FiZap, FiCheckCircle, FiRadio, FiFileText, FiLogOut, FiSave } = FiIcons;

export default function SettingsView() {
  const { settings: savedSettings, setSettings, resetAccount, notify } = useBot();
  const { user, signOut } = useAuth();

  // Settings are edited in a local draft and only applied on Save (no auto-save).
  // `settings` below aliases the draft so all the reads in this view see staged values.
  const [draft, setDraft] = useState(savedSettings);
  const settings = draft;
  const dirty = JSON.stringify(draft) !== JSON.stringify(savedSettings);

  // Re-baseline the draft when saved settings change from outside this view (e.g. the
  // cross-device hydrate on login, or right after Save). Edits never touch savedSettings
  // until Save, so this can't clobber an in-progress edit.
  useEffect(() => { setDraft(savedSettings); }, [savedSettings]);

  const [alertsInput, setAlertsInput] = useState(settings.profitAlerts?.join(',') || '50,100,200,500');
  const [lossAlertsInput, setLossAlertsInput] = useState(settings.lossAlerts?.join(',') || '10,20,30');
  const [wlAlertsInput, setWlAlertsInput] = useState(settings.watchlistAlerts?.join(',') || '10,25,50');

  // Raw string buffers for the numeric inputs so the field can be cleared while typing
  // (committing on blur). Without this, coercing every keystroke to a number forces a
  // stuck "0" you can't clear. Kept in sync with settings (e.g. after cross-device hydrate).
  const [hardSlInput, setHardSlInput] = useState(String(settings.hardStopLoss));
  const [trailSlInput, setTrailSlInput] = useState(String(settings.trailingStopLoss));
  const [trailActInput, setTrailActInput] = useState(String(settings.trailingActivationMultiplier));
  // Per-tier input drafts keyed `${idx}-${field}`; settings.takeProfitTiers stays numeric.
  const [tierDrafts, setTierDrafts] = useState({});

  useEffect(() => { setHardSlInput(String(settings.hardStopLoss)); }, [settings.hardStopLoss]);
  useEffect(() => { setTrailSlInput(String(settings.trailingStopLoss)); }, [settings.trailingStopLoss]);
  useEffect(() => { setTrailActInput(String(settings.trailingActivationMultiplier)); }, [settings.trailingActivationMultiplier]);

  const [showConfirmReset, setShowConfirmReset] = useState(false);

  // Commit a numeric field on blur; invalid/empty reverts to the last good value.
  const commitNumber = (key, raw, current, setInput) => {
    let num = parseFloat(raw);
    if (isNaN(num) || num < 0) num = current;
    setDraft(prev => ({ ...prev, [key]: num }));
    setInput(String(num));
  };

  const tierValue = (idx, field, fallback) =>
    tierDrafts[`${idx}-${field}`] ?? String(fallback);

  const handleTierDraft = (idx, field, raw) =>
    setTierDrafts(prev => ({ ...prev, [`${idx}-${field}`]: raw }));

  const commitTier = (idx, field, current) => {
    let num = parseFloat(tierDrafts[`${idx}-${field}`]);
    if (isNaN(num) || num < 0) num = current;
    const newTiers = [...(settings.takeProfitTiers || [])];
    newTiers[idx] = { ...newTiers[idx], [field]: num };
    setDraft(prev => ({ ...prev, takeProfitTiers: newTiers }));
    setTierDrafts(prev => { const n = { ...prev }; delete n[`${idx}-${field}`]; return n; });
  };

  const toggleSetting = (key) => setDraft(prev => ({ ...prev, [key]: prev[key] === false ? true : !prev[key] }));

  // Per-tier enable/disable (default on; a disabled tier is skipped by both engines).
  const toggleTier = (idx) => setDraft(prev => {
    const newTiers = [...(prev.takeProfitTiers || [])];
    newTiers[idx] = { ...newTiers[idx], enabled: newTiers[idx]?.enabled === false };
    return { ...prev, takeProfitTiers: newTiers };
  });

  const handleAlertsBlur = () => {
    const arr = alertsInput.split(',').map(s => parseFloat(s.trim())).filter(n => !isNaN(n) && n > 0).sort((a, b) => a - b);
    const finalArr = arr.length ? arr : [50, 100, 200];
    setDraft(prev => ({ ...prev, profitAlerts: finalArr }));
    setAlertsInput(finalArr.join(','));
  };

  const handleLossAlertsBlur = () => {
    const arr = lossAlertsInput.split(',').map(s => parseFloat(s.trim())).filter(n => !isNaN(n) && n > 0).sort((a, b) => a - b);
    const finalArr = arr.length ? arr : [10, 20, 30];
    setDraft(prev => ({ ...prev, lossAlerts: finalArr }));
    setLossAlertsInput(finalArr.join(','));
  };

  const handleWlAlertsBlur = () => {
    const arr = wlAlertsInput.split(',').map(s => parseFloat(s.trim())).filter(n => !isNaN(n) && n > 0).sort((a, b) => a - b);
    const finalArr = arr.length ? arr : [10, 25, 50];
    setDraft(prev => ({ ...prev, watchlistAlerts: finalArr }));
    setWlAlertsInput(finalArr.join(','));
  };

  // Apply the staged draft to the real settings (persists to localStorage + Supabase).
  const handleSave = () => {
    setSettings(draft);
    notify('Settings saved', 'success');
  };

  // Throw away staged edits and restore the inputs to the last saved values.
  const handleDiscard = () => {
    setDraft(savedSettings);
    setTierDrafts({});
    setAlertsInput(savedSettings.profitAlerts?.join(',') || '50,100,200,500');
    setLossAlertsInput(savedSettings.lossAlerts?.join(',') || '10,20,30');
    setWlAlertsInput(savedSettings.watchlistAlerts?.join(',') || '10,25,50');
  };

  const confirmReset = () => {
    resetAccount();
    setShowConfirmReset(false);
  };

  return (
    <div className="p-4 min-h-[calc(100vh-140px)] lg:min-h-[calc(100vh-40px)] pb-28">
      <div className="flex items-center gap-2 mb-6">
        <SafeIcon icon={FiSettings} className="text-white text-xl" />
        <h2 className="text-white font-bold text-xl">Bot Rules Engine</h2>
      </div>

      <div className="space-y-4">
        {/* Notifications */}
        <div className="bg-[#1c1c1d] border border-[#2c2c2e] rounded-xl p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 text-white font-semibold mb-1">
                <SafeIcon icon={settings.notificationsEnabled !== false ? FiBell : FiBellOff} className="text-yellow-400" /> Notifications
              </div>
              <p className="text-gray-400 text-xs leading-relaxed">
                Show in-app toast popups for trades, alerts, and stop-loss events. When off, events are still recorded in the activity log (bell icon).
              </p>
            </div>
            <button
              onClick={() => toggleSetting('notificationsEnabled')}
              className={`relative w-10 h-5 rounded-full transition-colors shrink-0 ${settings.notificationsEnabled !== false ? 'bg-yellow-500' : 'bg-[#3c3c3e]'}`}
            >
              <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${settings.notificationsEnabled !== false ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </button>
          </div>
        </div>

        {/* Watchlist Alerts */}
        <div className="bg-[#1c1c1d] border border-blue-500/30 rounded-xl p-4 shadow-[0_0_15px_rgba(59,130,246,0.05)]">
          <div className="flex flex-col gap-3 mb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-white font-semibold">
                <SafeIcon icon={FiEye} className="text-blue-400" /> Watchlist Alerts
              </div>
            </div>
            <div className="flex items-center justify-between bg-black/40 p-2 rounded-lg border border-[#2c2c2e]">
              <span className="text-xs text-gray-400 flex items-center gap-1">
                <SafeIcon icon={FiBell} /> Alert Targets (±%):
              </span>
              <div className="flex items-center gap-1">
                <input 
                  type="text" 
                  value={wlAlertsInput}
                  onChange={(e) => setWlAlertsInput(e.target.value)}
                  onBlur={handleWlAlertsBlur}
                  placeholder="10,25,50"
                  className="w-32 bg-transparent text-blue-400 font-mono text-sm focus:outline-none text-right placeholder-gray-600"
                />
                <SafeIcon icon={FiEdit2} className="text-gray-500 text-xs ml-1" />
              </div>
            </div>
          </div>
          <p className="text-gray-400 text-sm leading-relaxed mb-1">
            Get notified when a watched token moves up or down by these percentages since you added it.
          </p>
        </div>

        {/* Profit Maximization Alerts */}
        <div className="bg-[#1c1c1d] border border-green-500/30 rounded-xl p-4 shadow-[0_0_15px_rgba(74,222,128,0.05)]">
          <div className="flex flex-col gap-3 mb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-white font-semibold">
                <SafeIcon icon={FiAward} className="text-green-400" /> Position Profit Alerts
              </div>
            </div>
            <div className="flex items-center justify-between bg-black/40 p-2 rounded-lg border border-[#2c2c2e]">
              <span className="text-xs text-gray-400 flex items-center gap-1">
                <SafeIcon icon={FiBell} /> Alert Targets (%):
              </span>
              <div className="flex items-center gap-1">
                <input 
                  type="text" 
                  value={alertsInput}
                  onChange={(e) => setAlertsInput(e.target.value)}
                  onBlur={handleAlertsBlur}
                  placeholder="50,100,200"
                  className="w-32 bg-transparent text-green-400 font-mono text-sm focus:outline-none text-right placeholder-gray-600"
                />
                <SafeIcon icon={FiEdit2} className="text-gray-500 text-xs ml-1" />
              </div>
            </div>
          </div>
          <p className="text-gray-400 text-sm leading-relaxed mb-1">
            Receive instant push notifications when an active paper position hits these profit milestones.
          </p>
        </div>

        {/* Loss Mitigation Alerts */}
        <div className="bg-[#1c1c1d] border border-red-500/30 rounded-xl p-4 shadow-[0_0_15px_rgba(248,113,113,0.05)]">
          <div className="flex flex-col gap-3 mb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-white font-semibold">
                <SafeIcon icon={FiTrendingDown} className="text-red-400" /> Position Loss Alerts
              </div>
            </div>
            <div className="flex items-center justify-between bg-black/40 p-2 rounded-lg border border-[#2c2c2e]">
              <span className="text-xs text-gray-400 flex items-center gap-1">
                <SafeIcon icon={FiBell} /> Alert Targets (%):
              </span>
              <div className="flex items-center gap-1">
                <span className="text-red-400 font-mono text-sm">-</span>
                <input 
                  type="text" 
                  value={lossAlertsInput}
                  onChange={(e) => setLossAlertsInput(e.target.value)}
                  onBlur={handleLossAlertsBlur}
                  placeholder="10,20,30"
                  className="w-24 bg-transparent text-red-400 font-mono text-sm focus:outline-none text-right placeholder-gray-600"
                />
                <SafeIcon icon={FiEdit2} className="text-gray-500 text-xs ml-1" />
              </div>
            </div>
          </div>
          <p className="text-gray-400 text-sm leading-relaxed mb-1">
            Receive push notifications when an active paper position drops by these percentages below entry.
          </p>
        </div>

        {/* Auto Take-Profit */}
        <div className="bg-[#1c1c1d] border border-green-500/30 rounded-xl p-4 shadow-[0_0_15px_rgba(74,222,128,0.05)]">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-white font-semibold">
              <SafeIcon icon={FiTarget} className="text-green-400" /> Auto Take-Profit
            </div>
            <button
              onClick={() => setDraft(prev => ({ ...prev, takeProfitEnabled: !prev.takeProfitEnabled }))}
              className={`relative w-10 h-5 rounded-full transition-colors shrink-0 ${settings.takeProfitEnabled ? 'bg-green-500' : 'bg-[#3c3c3e]'}`}
            >
              <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${settings.takeProfitEnabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </button>
          </div>

          {settings.takeProfitEnabled && (
            <div className="space-y-2 mb-3">
              {(settings.takeProfitTiers || []).map((tier, idx) => {
                const tierOn = tier.enabled !== false;
                return (
                <div key={idx} className="flex items-center gap-2 bg-black/40 p-2 rounded-lg border border-[#2c2c2e]">
                  <span className="text-[10px] text-gray-500 uppercase w-12 shrink-0 font-semibold">Tier {idx + 1}</span>
                  <div className={`flex items-center gap-2 flex-1 transition-opacity ${tierOn ? '' : 'opacity-40'}`}>
                    <span className="text-[10px] text-gray-500">At</span>
                    <div className="flex items-center gap-0.5 bg-[#2c2c2e] border border-[#3c3c3e] rounded px-1.5 py-0.5">
                      <input
                        type="number"
                        value={tierValue(idx, 'at', tier.at)}
                        disabled={!tierOn}
                        onChange={(e) => handleTierDraft(idx, 'at', e.target.value)}
                        onBlur={() => commitTier(idx, 'at', tier.at)}
                        className="w-10 bg-transparent text-green-400 font-mono text-xs focus:outline-none text-right disabled:cursor-not-allowed"
                      />
                      <span className="text-gray-500 text-xs">%</span>
                    </div>
                    <span className="text-[10px] text-gray-500">→ sell</span>
                    <div className="flex items-center gap-0.5 bg-[#2c2c2e] border border-[#3c3c3e] rounded px-1.5 py-0.5">
                      <input
                        type="number"
                        value={tierValue(idx, 'sellPct', tier.sellPct)}
                        disabled={!tierOn}
                        onChange={(e) => handleTierDraft(idx, 'sellPct', e.target.value)}
                        onBlur={() => commitTier(idx, 'sellPct', tier.sellPct)}
                        className="w-10 bg-transparent text-yellow-400 font-mono text-xs focus:outline-none text-right disabled:cursor-not-allowed"
                      />
                      <span className="text-gray-500 text-xs">%</span>
                    </div>
                  </div>
                  <button
                    onClick={() => toggleTier(idx)}
                    title={tierOn ? 'Disable this tier' : 'Enable this tier'}
                    className={`relative w-9 h-5 rounded-full transition-colors shrink-0 ${tierOn ? 'bg-green-500' : 'bg-[#3c3c3e]'}`}
                  >
                    <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${tierOn ? 'translate-x-4' : 'translate-x-0.5'}`} />
                  </button>
                </div>
                );
              })}
            </div>
          )}

          <p className="text-gray-400 text-sm leading-relaxed">
            Automatically sells a portion of each position when it hits these profit targets. Locks in gains without manual intervention.
          </p>
        </div>

        {/* Hard Stop Loss */}
        <div className="bg-[#1c1c1d] border border-[#2c2c2e] rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-white font-semibold">
              <SafeIcon icon={FiShield} className="text-orange-400" /> Hard Stop-Loss
            </div>
            <div className="flex items-center gap-2">
              <div className={`flex items-center gap-1 bg-[#2c2c2e] px-2 py-1 rounded border border-[#3c3c3e] transition-opacity ${settings.hardStopEnabled === false ? 'opacity-40' : ''}`}>
                <span className="text-orange-400 font-mono text-sm pl-1">-</span>
                <input
                  type="number"
                  value={hardSlInput}
                  disabled={settings.hardStopEnabled === false}
                  onChange={(e) => setHardSlInput(e.target.value)}
                  onBlur={() => commitNumber('hardStopLoss', hardSlInput, settings.hardStopLoss, setHardSlInput)}
                  className="w-10 bg-transparent text-orange-400 font-mono text-sm focus:outline-none text-center disabled:cursor-not-allowed"
                />
                <span className="text-orange-400 font-mono text-sm pr-1">%</span>
                <SafeIcon icon={FiEdit2} className="text-gray-500 text-xs ml-1" />
              </div>
              <button
                onClick={() => toggleSetting('hardStopEnabled')}
                className={`relative w-10 h-5 rounded-full transition-colors shrink-0 ${settings.hardStopEnabled !== false ? 'bg-orange-500' : 'bg-[#3c3c3e]'}`}
              >
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${settings.hardStopEnabled !== false ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
            </div>
          </div>
          <p className="text-gray-400 text-sm leading-relaxed">
            The bot will automatically sell 100% of the token if the price drops by {settings.hardStopLoss}% from your initial buy price.
          </p>
          <div className="mt-3 flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold">
            <SafeIcon icon={FiLock} className={settings.hardStopEnabled !== false ? 'text-gray-500' : 'text-gray-600'} />
            <span className={settings.hardStopEnabled !== false ? 'text-gray-500' : 'text-gray-600'}>
              {settings.hardStopEnabled !== false ? 'Active Rule' : 'Disabled'}
            </span>
          </div>
        </div>

        {/* Trailing Stop Loss */}
        <div className="bg-[#1c1c1d] border border-[#2c2c2e] rounded-xl p-4">
          <div className="flex flex-col gap-3 mb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-white font-semibold">
                <SafeIcon icon={FiTrendingUp} className="text-blue-400" /> Trailing Stop-Loss
              </div>
              <div className="flex items-center gap-2">
                <div className={`flex items-center gap-1 bg-[#2c2c2e] px-2 py-1 rounded border border-[#3c3c3e] transition-opacity ${settings.trailingStopEnabled === false ? 'opacity-40' : ''}`}>
                  <span className="text-blue-400 font-mono text-sm pl-1">-</span>
                  <input
                    type="number"
                    value={trailSlInput}
                    disabled={settings.trailingStopEnabled === false}
                    onChange={(e) => setTrailSlInput(e.target.value)}
                    onBlur={() => commitNumber('trailingStopLoss', trailSlInput, settings.trailingStopLoss, setTrailSlInput)}
                    className="w-10 bg-transparent text-blue-400 font-mono text-sm focus:outline-none text-center disabled:cursor-not-allowed"
                  />
                  <span className="text-blue-400 font-mono text-sm pr-1">%</span>
                  <SafeIcon icon={FiEdit2} className="text-gray-500 text-xs ml-1" />
                </div>
                <button
                  onClick={() => toggleSetting('trailingStopEnabled')}
                  className={`relative w-10 h-5 rounded-full transition-colors shrink-0 ${settings.trailingStopEnabled !== false ? 'bg-blue-500' : 'bg-[#3c3c3e]'}`}
                >
                  <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${settings.trailingStopEnabled !== false ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </button>
              </div>
            </div>

            <div className={`flex items-center justify-between bg-black/40 p-2 rounded-lg border border-[#2c2c2e] transition-opacity ${settings.trailingStopEnabled === false ? 'opacity-40' : ''}`}>
              <span className="text-xs text-gray-400">Activates at multiplier:</span>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  step="0.1"
                  value={trailActInput}
                  disabled={settings.trailingStopEnabled === false}
                  onChange={(e) => setTrailActInput(e.target.value)}
                  onBlur={() => commitNumber('trailingActivationMultiplier', trailActInput, settings.trailingActivationMultiplier, setTrailActInput)}
                  className="w-12 bg-transparent text-white font-mono text-sm focus:outline-none text-right disabled:cursor-not-allowed"
                />
                <span className="text-gray-500 font-mono text-sm">x</span>
                <SafeIcon icon={FiEdit2} className="text-gray-500 text-xs ml-1" />
              </div>
            </div>
          </div>
          
          <p className="text-gray-400 text-sm leading-relaxed mb-3">
            Only activates after the token reaches <strong className="text-white">{settings.trailingActivationMultiplier}x</strong> the original buy price. Once active, it sells if the price drops {settings.trailingStopLoss}% from its new all-time high.
          </p>
          
          <div className="h-1.5 w-full bg-[#2c2c2e] rounded-full overflow-hidden flex">
            <div className="h-full bg-blue-500/30 w-1/2 relative">
              <div className="absolute right-0 top-0 h-full w-0.5 bg-blue-500"></div>
            </div>
            <div className="h-full bg-transparent w-1/2"></div>
          </div>
          <div className="flex justify-between text-[10px] text-gray-500 mt-1 font-mono">
            <span>1x (Buy)</span>
            <span className="text-blue-400">{settings.trailingActivationMultiplier}x (Activates)</span>
            <span>3x+</span>
          </div>
        </div>

        {/* Trading Mode */}
        <div className="bg-[#1c1c1d] border border-blue-500/30 rounded-xl p-4 shadow-[0_0_15px_rgba(59,130,246,0.05)]">
          <div className="flex items-center gap-2 text-white font-semibold mb-4">
            <SafeIcon icon={FiRadio} className="text-blue-400" /> Trading Mode
          </div>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <button
              onClick={() => setDraft(prev => ({ ...prev, tradingMode: 'paper' }))}
              className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl border font-semibold text-sm transition-colors ${
                settings.tradingMode !== 'live'
                  ? 'bg-gray-500/20 border-gray-500/50 text-white'
                  : 'bg-transparent border-[#2c2c2e] text-gray-500 hover:border-gray-500/30 hover:text-gray-400'
              }`}
            >
              <SafeIcon icon={FiFileText} className="text-lg" />
              Paper Mode
            </button>
            <button
              onClick={() => {
                if (!isBotConfigured()) {
                  notify('Backend bot not configured (set VITE_BOT_API_URL). Live trading is unavailable.', 'error');
                  return;
                }
                setDraft(prev => ({ ...prev, tradingMode: 'live' }));
              }}
              className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl border font-semibold text-sm transition-colors ${
                settings.tradingMode === 'live'
                  ? 'bg-green-500/20 border-green-500/50 text-green-400'
                  : 'bg-transparent border-[#2c2c2e] text-gray-500 hover:border-green-500/30 hover:text-green-400/70'
              }`}
            >
              <div className="relative">
                <SafeIcon icon={FiZap} className="text-lg" />
                {settings.tradingMode === 'live' && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                )}
              </div>
              Live Mode
            </button>
          </div>
          <p className="text-gray-400 text-xs leading-relaxed">
            {settings.tradingMode === 'live'
              ? 'Live Mode active. The backend bot executes real Jupiter swaps from your burner wallet and auto-sells on your SL/TP rules.'
              : 'Paper Mode active. All trades are simulated with virtual SOL. No real money moves.'}
          </p>
        </div>

        {/* API Integrations */}
        <div className="bg-[#1c1c1d] border border-purple-500/30 rounded-xl p-4 shadow-[0_0_15px_rgba(168,85,247,0.05)]">
          <div className="flex items-center gap-2 text-white font-semibold mb-4">
            <SafeIcon icon={FiZap} className="text-purple-400" /> API Integrations
          </div>

          {/* Helius Toggle */}
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="flex-1">
              <p className="text-white text-sm font-medium mb-0.5">Real-Time Price Triggers</p>
              <p className="text-gray-400 text-xs leading-relaxed">
                Helius WebSocket monitors on-chain swap activity for your open positions and fires a price refresh instantly — instead of waiting for the next poll cycle.
              </p>
            </div>
            <button
              onClick={() => setDraft(prev => ({ ...prev, heliusEnabled: !prev.heliusEnabled }))}
              className={`relative w-10 h-5 rounded-full transition-colors shrink-0 mt-0.5 ${settings.heliusEnabled ? 'bg-purple-500' : 'bg-[#3c3c3e]'}`}
            >
              <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${settings.heliusEnabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </button>
          </div>

          {/* Birdeye Toggle */}
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="flex-1">
              <p className="text-white text-sm font-medium mb-0.5">Token Security Analysis</p>
              <p className="text-gray-400 text-xs leading-relaxed">
                Birdeye checks tokens for honeypot risk, holder concentration, and freeze flags when you look up a token before buying.
              </p>
            </div>
            <button
              onClick={() => setDraft(prev => ({ ...prev, birdeyeEnabled: !prev.birdeyeEnabled }))}
              className={`relative w-10 h-5 rounded-full transition-colors shrink-0 mt-0.5 ${settings.birdeyeEnabled ? 'bg-purple-500' : 'bg-[#3c3c3e]'}`}
            >
              <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${settings.birdeyeEnabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </button>
          </div>

          <div className="flex items-center gap-1.5 text-[11px] text-gray-500 bg-black/30 rounded-lg px-3 py-2 border border-[#2c2c2e]">
            <SafeIcon icon={FiCheckCircle} className="text-gray-600 shrink-0" />
            API keys are loaded from your <span className="font-mono text-gray-400 ml-0.5">.env</span> file
            &nbsp;(<span className="font-mono text-gray-400">VITE_HELIUS_API_KEY</span>,&nbsp;
            <span className="font-mono text-gray-400">VITE_BIRDEYE_API_KEY</span>).
          </div>
        </div>

        {/* Account */}
        <div className="bg-[#1c1c1d] border border-[#2c2c2e] rounded-xl p-4">
          <div className="flex items-center gap-2 text-white font-semibold mb-3">
            <SafeIcon icon={FiLock} className="text-blue-400" /> Account
          </div>
          {user?.email && (
            <p className="text-gray-400 text-sm mb-4">
              Signed in as <span className="text-white font-medium">{user.email}</span>
            </p>
          )}
          <button
            onClick={() => signOut()}
            className="w-full bg-[#2c2c2e] hover:bg-[#3c3c3e] text-gray-300 hover:text-white border border-[#3c3c3e] font-bold py-3 rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            <SafeIcon icon={FiLogOut} /> Sign Out
          </button>
        </div>

        {/* Data Management */}
        <div className="bg-[#1c1c1d] border border-red-500/20 rounded-xl p-4">
          <div className="flex items-center gap-2 text-white font-semibold mb-3">
            <SafeIcon icon={FiDatabase} className="text-red-400" /> Data Management
          </div>
          <p className="text-gray-400 text-sm leading-relaxed mb-4">
            Resetting your account will delete all trade history, active positions, and watchlist. It will restore your simulated starting balance to 100 SOL.
          </p>
          <button 
            onClick={() => setShowConfirmReset(true)}
            className="w-full bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 font-bold py-3 rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            <SafeIcon icon={FiTrash2} /> Reset Paper Trading Account
          </button>
        </div>

        {/* Info Note */}
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 text-blue-400 text-xs leading-relaxed">
          <strong>Live Engine Connected:</strong> The bot fetches real-time prices from DexScreener. Refresh rate adapts automatically — 3s when a position is near stop-loss, 5s when positions are open, 10s when only watching. Stop-losses, auto take-profits, and alerts all trigger on live market prices.
        </div>
      </div>

      {/* Save / Discard bar — settings only apply when saved (no auto-save) */}
      <AnimatePresence>
        {dirty && (
          <motion.div
            key="settings-save-bar"
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            className="fixed bottom-[75px] lg:bottom-4 left-0 right-0 max-w-md lg:max-w-2xl mx-auto px-4 z-30"
          >
            <div className="bg-[#1c1c1d] border border-blue-500/40 rounded-2xl shadow-[0_-5px_25px_rgba(0,0,0,0.5)] p-3 flex items-center justify-between gap-3">
              <span className="text-xs text-gray-300 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" /> Unsaved changes
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleDiscard}
                  className="px-4 py-2 rounded-xl text-sm font-medium text-gray-400 hover:text-white bg-[#2c2c2e] hover:bg-[#3c3c3e] border border-[#3c3c3e] transition-colors"
                >
                  Discard
                </button>
                <button
                  onClick={handleSave}
                  className="px-5 py-2 rounded-xl text-sm font-bold text-white bg-blue-600 hover:bg-blue-500 border border-blue-500 shadow-lg shadow-blue-500/20 transition-colors flex items-center gap-2"
                >
                  <SafeIcon icon={FiSave} /> Save
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Confirmation Modal */}
      <AnimatePresence>
        {showConfirmReset && (
          <>
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 z-[70] backdrop-blur-sm"
              onClick={() => setShowConfirmReset(false)}
            />
            <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 pointer-events-none">
              <motion.div 
                initial={{ scale: 0.95, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.95, opacity: 0, y: 20 }}
                className="bg-[#1c1c1d] border border-red-500/30 rounded-2xl p-6 max-w-sm w-full pointer-events-auto shadow-[0_10px_40px_rgba(248,113,113,0.2)]"
              >
                <div className="flex flex-col items-center text-center mb-6">
                  <div className="w-14 h-14 bg-red-500/10 rounded-full flex items-center justify-center mb-4 border border-red-500/20">
                    <SafeIcon icon={FiAlertTriangle} className="text-3xl text-red-400" />
                  </div>
                  <h3 className="text-white font-bold text-xl mb-2">Are you completely sure?</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    This will permanently delete all your paper trade history, active positions, and reset your simulated balance back to 100 SOL.
                  </p>
                </div>
                
                <div className="flex gap-3">
                  <button 
                    onClick={() => setShowConfirmReset(false)}
                    className="flex-1 py-3 rounded-xl font-medium text-gray-400 hover:text-white bg-[#2c2c2e] hover:bg-[#3c3c3e] transition-colors border border-[#3c3c3e]"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={confirmReset}
                    className="flex-1 py-3 rounded-xl font-bold text-white bg-red-600 hover:bg-red-500 transition-colors shadow-lg shadow-red-500/20 border border-red-500"
                  >
                    Yes, Reset All
                  </button>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>

    </div>
  );
}